import { CalendarDate, parseZonedDateTime } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  enumSalesLoadLineItemPhase,
  enumUnit,
  LoadSortOption,
  LoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { weightToPounds } from '@rematter/ui-web';

import { enumLoadWeightType, LoadRecord } from '../types/load';
import { toMoney } from '../utils/money';
import { convertWeightForPricing } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const loadsListQueryAtom = createQueryAtomFactory(
  'trade.loadsList',
  (params: {
    ids?: string[];
    status?: LoadStatus[];
    searchTerm?: string;
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
    facilityId?: string;
    facilityIds?: string[];
    salesOrderIds?: string[];
    purchaseOrderId?: string;
    accountIds?: string[];
    createdByUserIds?: string[];
    fromColumnDate?: CalendarDate;
    toColumnDate?: CalendarDate;
    columnStatuses?: LoadStatus[];
    bookingId?: string;
    salesOrderId?: string;
    limit?: number;
    offset?: number;
    sorting?: LoadSortOption[];
  }) => ({
    loads: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.columnStatuses || params.status,
          searchTerm: params.searchTerm || undefined,
          salesOrderId: params.salesOrderId,
          salesOrderIds: params.salesOrderIds,
          purchaseOrderIds: params.purchaseOrderId
            ? [params.purchaseOrderId]
            : undefined,
          accountIds: params.accountIds,
          createdBy: params.createdByUserIds,
          fromExpectedShipDate: (
            params.fromColumnDate || params.fromExpectedShipDate
          )?.toString(),
          toExpectedShipDate: (
            params.toColumnDate || params.toExpectedShipDate
          )?.toString(),
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          bookingIds: params.bookingId ? [params.bookingId] : undefined,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        status: true,
        reviewed: true,
        expectedShipDate: true,
        customerAccount: {
          name: true,
        },
        shippedDate: true,
        documentNumber: true,
        salesOrder: {
          documentNumber: true,
          account: {
            id: true,
            name: true,
          },
        },
        booking: {
          id: true,
          documentNumber: true,
        },
        lineItems: {
          id: true,
          itemId: true,
          item: {
            id: true,
            name: true,
          },
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          pricingUnit: true,
          phase: true,
          weights: true,
        },
        reconciliation: {
          ticketNumber: true,
        },
        facilityId: true,
        shippingCarrierName: true,
        releaseNumber: true,
        containerNumber: true,
        createdBy: true,
        createdByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        timestamps: {
          createdAt: true,
        },
      },
    },
  }),
  {
    select: ({
      loads,
    }): {
      total: number;
      loads: LoadRecord[];
    } => ({
      loads: loads.entries.map((load) => {
        const shippedLineItems = load.lineItems.filter(
          (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
        );

        const requestedLineItems = load.lineItems.filter(
          (item) =>
            item.phase === enumSalesLoadLineItemPhase.Requested || !item.phase,
        );

        // Requested or Shipped net quantity based on status
        const relevantLineItems = shippedLineItems?.length
          ? shippedLineItems
          : requestedLineItems;

        const netQuantity = relevantLineItems.reduce(
          (acc, lineItem) =>
            acc +
            Number(lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0),
          0,
        );

        const shippedTotal = shippedLineItems.reduce((sum, li) => {
          const netWeightPounds = weightToPounds(
            li.weights?.[enumLoadWeightType.Net],
          );
          const pricingUnit = li.pricingUnit ?? enumUnit.Pounds;
          const convertedWeight = convertWeightForPricing(
            netWeightPounds,
            pricingUnit,
          );
          return sum + (li.unitPrice?.amount ?? 0) * convertedWeight;
        }, 0);

        return {
          id: load.id,
          loadNumber: load.documentNumber.toString(),
          status: load.status as LoadStatus,
          reviewed: load.reviewed ?? false,
          customerName: load.customerAccount?.name,
          expectedShipDate: maybeParseCalendarDate(load.expectedShipDate),
          shippedDate: maybeParseCalendarDate(load.shippedDate),
          loadReceivedDate: undefined,
          salesOrderId: undefined,
          salesOrderNumber: load.salesOrder?.documentNumber ?? undefined,
          salesOrder: undefined,
          bookingId: load.booking?.id ?? undefined,
          bookingNumber: load.booking?.documentNumber ?? undefined,
          facilityId: load.facilityId ?? undefined,
          shippingCarrierName: load.shippingCarrierName ?? undefined,
          scac: undefined,
          freightForwarder: undefined,
          truckFreight: undefined,
          deliveryNumber: undefined,
          releaseNumber: load.releaseNumber ?? undefined,
          truckNumber: undefined,
          trailerNumber: undefined,
          containerNumber: load.containerNumber ?? undefined,
          sealNumber: undefined,
          containerTypeId: undefined,
          containerType: undefined,
          internalNotes: undefined,
          externalNotes: undefined,
          invoiceId: undefined,
          accountId: load.salesOrder?.account?.id ?? undefined,
          customer: load.salesOrder?.account?.name ?? undefined,
          receivingTicketNumber: load.reconciliation?.ticketNumber ?? undefined,
          shippedTotal,
          files: undefined,
          lineItems: load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            itemId: lineItem.itemId ?? null,
            type: 'Raw' as const,
            loadId: null,
            item: lineItem.item ?? null,
            tagId: '',
            unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
            estimatedPrice: null,
            pricingUnit: lineItem.pricingUnit,
            phase: lineItem.phase,
            weights: lineItem.weights as Record<string, Weight>,
          })),
          lineItemCount: load.lineItems.length,
          netRequestedQuantity: requestedLineItems.reduce(
            (acc, lineItem) =>
              acc +
              Number(lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0),
            0,
          ),
          netQuantity,
          createdBy: load.createdBy ?? undefined,
          createdByUser: load.createdByUser || null,
          timestamps: {
            createdAt: parseZonedDateTime(load.timestamps.createdAt),
          },
        };
      }),
      total: loads.page.total ?? 0,
    }),
  },
);
